/**
 * The living Sun (Sprint 2, S2-08): limb darkening and slowly boiling granulation on the Sun's sphere. Output is HDR
 * (`uGain` > 1 with post-processing) so the disc blooms. Supports the logarithmic depth buffer.
 */
import * as THREE from 'three';
import { NOISE_GLSL } from './shaders';

/** Linear limb-darkening coefficient (visible light, ≈ 0.6 for the Sun). */
const LIMB_U = 0.6;

export function createSunMaterial(color: THREE.Color): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: color.clone() },
      uGain: { value: 1 },
      uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      varying vec3 vNormalV;
      varying vec3 vViewDir;
      varying vec3 vObj;
      void main() {
        vObj = position;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vNormalV = normalize(normalMatrix * normal);
        vViewDir = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_fragment>
      uniform vec3 uColor;
      uniform float uGain;
      uniform float uTime;
      varying vec3 vNormalV;
      varying vec3 vViewDir;
      varying vec3 vObj;
      ${NOISE_GLSL}
      void main() {
        #include <logdepthbuf_fragment>
        float mu = clamp(dot(normalize(vNormalV), normalize(vViewDir)), 0.0, 1.0);
        float limb = 1.0 - ${LIMB_U.toFixed(2)} * (1.0 - mu);
        // granulation: fine cells drifting slowly; faculae: broad bright patches
        float cells = zfFbm(vObj * 18.0 + vec3(0.0, 0.0, uTime * 0.04));
        float patches = zfNoise(vObj * 3.0 + vec3(uTime * 0.01));
        float surface = 0.86 + 0.22 * cells + 0.08 * patches;
        // white-hot centre, orange limb: tone mapping drives saturated over-bright colours towards pink, so the core is
        // kept near white and only the limb carries the catalogue colour
        vec3 core = mix(uColor, vec3(1.0, 0.93, 0.8), 0.75);
        vec3 c = mix(uColor * vec3(1.0, 0.75, 0.55), core, smoothstep(0.0, 0.8, mu)) * limb * surface;
        gl_FragColor = vec4(c * uGain, 1.0);
        #include <colorspace_fragment>
      }`,
  });
}
